import { X } from "@phosphor-icons/react";
import { Dialog as DialogPrimitive } from "radix-ui";
import type { ReactNode } from "react";
import { Button } from "~/foundation/components/Button";
import { IconButton } from "~/foundation/components/IconButton";
import { depths } from "~/foundation/styles/depths";
import { cn } from "~/foundation/utils/cn";

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Accessible label for the close control — must be translated by the caller. */
  closeLabel: string;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "full" | "sheet";
  children?: ReactNode;
}

/**
 * Every size caps at the viewport and scrolls its BODY (title and footer stay put): a tall dialog used to grow past
 * the screen with nothing to scroll (the thinker dialog with its facets, 2026-10-10). `sheet` is a full-screen sheet
 * below `md` and a wide dialog above it — for editors with tabs.
 */
const SIZES = {
  sm: "max-w-sm max-h-[90vh]",
  md: "max-w-md max-h-[90vh]",
  lg: "max-w-lg max-h-[90vh]",
  /** Detail views that are really small tables — a run's steps, a flow's nodes and versions. */
  xl: "max-w-3xl max-h-[90vh]",
  /** Wide multi-tab drill-downs (the command ledger). */
  full: "max-w-[min(1200px,95vw)] max-h-[90vh]",
  /** Full-screen on a phone, a wide dialog from `md` up. */
  sheet:
    "max-w-none h-full max-h-full rounded-none md:h-auto md:max-h-[90vh] md:max-w-3xl md:rounded-md",
} as const;

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  closeLabel,
  footer,
  size = "lg",
  children,
}: DialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="slate-overlay fixed inset-0 bg-backdrop"
          style={{ zIndex: depths.dialogOverlay }}
        />
        {/* Flex centering rather than a translate: it is direction-agnostic by construction. */}
        <div
          className={cn(
            "pointer-events-none fixed inset-0 flex items-center justify-center",
            size === "sheet" ? "p-0 md:p-4" : "p-4",
          )}
          style={{ zIndex: depths.dialog }}
        >
          <DialogPrimitive.Content
            className={cn(
              "slate-pop pointer-events-auto flex w-full min-h-0 flex-col",
              "rounded-md border border-divider bg-menu-bg p-5 shadow-menu",
              SIZES[size],
            )}
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <DialogPrimitive.Title className="text-h3 font-semibold text-text">
                  {title}
                </DialogPrimitive.Title>
                {description ? (
                  <DialogPrimitive.Description className="mt-1 text-body text-text-secondary">
                    {description}
                  </DialogPrimitive.Description>
                ) : null}
              </div>
              <DialogPrimitive.Close asChild>
                <IconButton icon={X} aria-label={closeLabel} size="sm" />
              </DialogPrimitive.Close>
            </div>

            {children ? (
              <div className="mt-4 min-h-0 flex-1 overflow-y-auto">{children}</div>
            ) : null}
            {footer ? <div className="mt-5 flex shrink-0 justify-end gap-2">{footer}</div> : null}
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel: string;
  cancelLabel: string;
  closeLabel: string;
  dangerous?: boolean;
  busy?: boolean;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  closeLabel,
  dangerous,
  busy,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="sm"
      closeLabel={closeLabel}
      {...(description === undefined ? {} : { description })}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button variant={dangerous ? "danger" : "primary"} disabled={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}
